import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider } from '@/context/AuthContext';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import '@/App.css';
import LandingPage from '@/pages/LandingPage';
import AboutPage from '@/pages/AboutPage';
import Dashboard from '@/pages/Dashboard';
import UnitDetail from '@/pages/UnitDetail';
import DailyQuestions from '@/pages/DailyQuestions';
import LessonPlanBuilder from '@/pages/LessonPlanBuilder';
import SkeletonNotes from '@/pages/SkeletonNotes';
import RubricGenerator from '@/pages/RubricGenerator';
import PrimarySources from '@/pages/PrimarySources';
import AnnotationTool from '@/pages/AnnotationTool';
import LectureNotes from '@/pages/LectureNotes';
import MapExplorer from '@/pages/MapExplorer';
import LoginPage from '@/pages/LoginPage';
import LecturePresentation from '@/pages/LecturePresentation';
import HistoricalTimeline from '@/pages/HistoricalTimeline';
import VocabularyQuiz from '@/pages/VocabularyQuiz';
import EssayGrader from '@/pages/EssayGrader';
import StateAssessment from '@/pages/StateAssessment';
import GeographyMap from '@/pages/GeographyMap';
import SkeletonNotesIndex from '@/pages/SkeletonNotesIndex';
import LectureIndex from '@/pages/LectureIndex';
import EssayGraderIndex from '@/pages/EssayGraderIndex';
import PrimarySourcesIndex from '@/pages/PrimarySourcesIndex';
import AdminUsers from '@/pages/AdminUsers';
import SubscriptionPage from '@/pages/SubscriptionPage';
import SubscriptionSuccess from '@/pages/SubscriptionSuccess';
import VideoIndex from '@/pages/VideoIndex';
import CreateUnit from '@/pages/CreateUnit';
import EditUnit from '@/pages/EditUnit';
import PrintableLesson from '@/pages/PrintableLesson';
import TourPage from '@/pages/TourPage';
import AuthCallbackPage from '@/pages/AuthCallbackPage';
import RubricGeneratorIndex from '@/pages/RubricGeneratorIndex';
import AIGenerateIndex from '@/pages/AIGenerateIndex';
import AdminLinkHealth from '@/pages/AdminLinkHealth';
import AtlasDashboard from '@/pages/AtlasDashboard';
import SearchPage from '@/pages/SearchPage';
import StandardsPage from '@/pages/StandardsPage';
import CollectionsPage from '@/pages/CollectionsPage';
import AdminMetricsPage from '@/pages/AdminMetricsPage';
import TeacherToolkitPage from '@/pages/TeacherToolkitPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import PrivacyPage from '@/pages/PrivacyPage';
import TermsPage from '@/pages/TermsPage';
import ContactPage from '@/pages/ContactPage';
import LibraryPage from '@/pages/LibraryPage';
import AdminCurriculumAudit from '@/pages/AdminCurriculumAudit';
import AdminActionResultPage from '@/pages/AdminActionResultPage';
import { USHistoryPage, WorldHistoryPage, CivicsPage, GovernmentPage } from '@/pages/SubjectPages';
import SettingsPage from '@/pages/SettingsPage';
import SyllabusPage from '@/pages/SyllabusPage';

const EDIT_ROUTE_PATTERNS = [
  /^\/create$/,
  /^\/edit\//,
  /^\/lesson-builder$/,
  /^\/rubric-generator\//,
  /^\/annotate\//,
];

function CopyProtectionGuard() {
  const { pathname } = useLocation();
  const isEditingRoute = EDIT_ROUTE_PATTERNS.some((re) => re.test(pathname));

  useEffect(() => {
    if (isEditingRoute) {
      document.body.classList.add('editing-mode');
      return undefined;
    }
    document.body.classList.remove('editing-mode');
    const prevent = (e) => {
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable) return;
      e.preventDefault();
    };
    document.addEventListener('copy', prevent);
    document.addEventListener('cut', prevent);
    document.addEventListener('contextmenu', prevent);
    return () => {
      document.removeEventListener('copy', prevent);
      document.removeEventListener('cut', prevent);
      document.removeEventListener('contextmenu', prevent);
    };
  }, [isEditingRoute]);

  return null;
}

function App() {
  return (
    <AuthProvider>
      <div className="grain-overlay" />
      <BrowserRouter>
        <CopyProtectionGuard />
        <Routes>
          {/* Public */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/tour" element={<TourPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/library" element={<ProtectedRoute><LibraryPage /></ProtectedRoute>} />
          <Route path="/print/lesson/:lessonId" element={<ProtectedRoute><PrintableLesson /></ProtectedRoute>} />
          <Route path="/admin/action-result" element={<AdminActionResultPage />} />
          <Route path="/auth/google/callback" element={<AuthCallbackPage />} />
          <Route path="/subscription" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
          <Route path="/subscription/success" element={<ProtectedRoute><SubscriptionSuccess /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
          <Route path="/syllabus/:grade" element={<ProtectedRoute requiredTier="pro"><SyllabusPage /></ProtectedRoute>} />

          {/* Basic tier — browse curriculum & units */}
          <Route path="/videos" element={<ProtectedRoute requiredTier="basic"><VideoIndex /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute requiredTier="basic"><AtlasDashboard /></ProtectedRoute>} />
          <Route path="/dashboard/:grade" element={<ProtectedRoute requiredTier="basic"><Dashboard /></ProtectedRoute>} />
          <Route path="/unit/:unitId" element={<ProtectedRoute requiredTier="basic"><UnitDetail /></ProtectedRoute>} />

          {/* Phase 2 — full-text search + subject hubs (filtered curriculum grids) */}
          <Route path="/search" element={<ProtectedRoute requiredTier="basic"><SearchPage /></ProtectedRoute>} />
          <Route path="/curriculum/us-history" element={<ProtectedRoute requiredTier="basic"><USHistoryPage /></ProtectedRoute>} />
          <Route path="/curriculum/world-history" element={<ProtectedRoute requiredTier="basic"><WorldHistoryPage /></ProtectedRoute>} />
          <Route path="/curriculum/civics" element={<ProtectedRoute requiredTier="basic"><CivicsPage /></ProtectedRoute>} />
          <Route path="/curriculum/government" element={<ProtectedRoute requiredTier="basic"><GovernmentPage /></ProtectedRoute>} />
          <Route path="/standards" element={<ProtectedRoute requiredTier="basic"><StandardsPage /></ProtectedRoute>} />
          <Route path="/collections" element={<ProtectedRoute requiredTier="basic"><CollectionsPage /></ProtectedRoute>} />
          <Route path="/teacher-toolkit" element={<ProtectedRoute requiredTier="basic"><TeacherToolkitPage /></ProtectedRoute>} />

          {/* Pro tier — all tools (view only) */}
          <Route path="/daily-questions" element={<ProtectedRoute requiredTier="pro"><DailyQuestions /></ProtectedRoute>} />
          <Route path="/skeleton-notes/:unitId" element={<ProtectedRoute requiredTier="pro"><SkeletonNotes /></ProtectedRoute>} />
          <Route path="/primary-sources/:unitId" element={<ProtectedRoute requiredTier="pro"><PrimarySources /></ProtectedRoute>} />
          <Route path="/annotate/:unitId/:sourceIndex" element={<ProtectedRoute requiredTier="pro"><AnnotationTool /></ProtectedRoute>} />
          <Route path="/maps" element={<ProtectedRoute requiredTier="pro"><MapExplorer /></ProtectedRoute>} />
          <Route path="/timeline" element={<ProtectedRoute requiredTier="pro"><HistoricalTimeline /></ProtectedRoute>} />
          <Route path="/geography" element={<ProtectedRoute requiredTier="pro"><GeographyMap /></ProtectedRoute>} />
          <Route path="/skeleton-notes-index" element={<ProtectedRoute requiredTier="pro"><SkeletonNotesIndex /></ProtectedRoute>} />
          <Route path="/skeleton-notes" element={<ProtectedRoute requiredTier="pro"><SkeletonNotesIndex /></ProtectedRoute>} />
          <Route path="/primary-sources-index" element={<ProtectedRoute requiredTier="pro"><PrimarySourcesIndex /></ProtectedRoute>} />
          <Route path="/quiz/:unitId" element={<ProtectedRoute requiredTier="pro"><VocabularyQuiz /></ProtectedRoute>} />
          <Route path="/lecture-notes/:unitId" element={<ProtectedRoute requiredTier="pro"><LectureNotes /></ProtectedRoute>} />
          <Route path="/lecture-presentation/:unitId" element={<ProtectedRoute requiredTier="pro"><LecturePresentation /></ProtectedRoute>} />
          <Route path="/essay-grader/:unitId" element={<ProtectedRoute requiredTier="pro"><EssayGrader /></ProtectedRoute>} />
          <Route path="/state-assessments" element={<ProtectedRoute requiredTier="pro"><StateAssessment /></ProtectedRoute>} />
          <Route path="/lecture-index" element={<ProtectedRoute requiredTier="pro"><LectureIndex /></ProtectedRoute>} />
          <Route path="/essay-grader-index" element={<ProtectedRoute requiredTier="pro"><EssayGraderIndex /></ProtectedRoute>} />

          {/* Admin only */}
          <Route path="/create" element={<ProtectedRoute requireAdmin><CreateUnit /></ProtectedRoute>} />
          <Route path="/edit/:unitId" element={<ProtectedRoute requireAdmin><EditUnit /></ProtectedRoute>} />
          <Route path="/lesson-builder" element={<ProtectedRoute requireAdmin><LessonPlanBuilder /></ProtectedRoute>} />
          <Route path="/rubric-generator" element={<ProtectedRoute requireAdmin><RubricGeneratorIndex /></ProtectedRoute>} />
          <Route path="/rubric-generator/:unitId" element={<ProtectedRoute requireAdmin><RubricGenerator /></ProtectedRoute>} />
          <Route path="/ai-generate" element={<ProtectedRoute requireAdmin><AIGenerateIndex /></ProtectedRoute>} />
          <Route path="/admin/link-health" element={<ProtectedRoute requireAdmin><AdminLinkHealth /></ProtectedRoute>} />
          <Route path="/admin/curriculum-audit" element={<ProtectedRoute requireAdmin><AdminCurriculumAudit /></ProtectedRoute>} />
          <Route path="/admin/users" element={<ProtectedRoute requireAdmin><AdminUsers /></ProtectedRoute>} />
          <Route path="/admin/metrics" element={<ProtectedRoute requireAdmin><AdminMetricsPage /></ProtectedRoute>} />
        </Routes>
      </BrowserRouter>
      <Toaster />
    </AuthProvider>
  );
}

export default App;
